import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Chess } from 'chess.js';
import { Chessboard } from 'react-chessboard';
import { 
  ArrowLeft, Trophy, Heart, RotateCcw, 
  Lightbulb, Volume2, VolumeX, Clock, Crown
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { useToast } from '../../hooks/use-toast';
import api from '../../utils/api';

const ChessGame = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // Game state
  const [game, setGame] = useState(new Chess());
  const [gamePosition, setGamePosition] = useState(game.fen());
  const [playerColor, setPlayerColor] = useState('white');
  const [isThinking, setIsThinking] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [winner, setWinner] = useState(null);
  const [moveHistory, setMoveHistory] = useState([]);
  const [soundEnabled, setSoundEnabled] = useState(true);
  
  // Session state
  const [session, setSession] = useState(null);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [result, setResult] = useState(null);
  const [endingGame, setEndingGame] = useState(false);
  
  // Click-to-move state
  const [selectedSquare, setSelectedSquare] = useState(null);
  const [possibleMoves, setPossibleMoves] = useState([]);
  
  // AI difficulty (affects search depth)
  const [aiDepth, setAiDepth] = useState(2); // Basic: 1, Medium: 2, Hard: 3

  // Load session on mount OR allow guest play
  useEffect(() => {
    const stored = localStorage.getItem('currentGameSession');
    if (stored) {
      const sessionData = JSON.parse(stored);
      if (sessionData.gameSlug === 'chess-master') {
        setSession(sessionData);
        // Set AI depth based on difficulty
        switch (sessionData.difficulty) {
          case 'basic': setAiDepth(1); break;
          case 'advance': setAiDepth(2); break;
          case 'super': setAiDepth(3); break;
          default: setAiDepth(2);
        }
      } else {
        // Allow guest play with default session
        setSession({ 
          gameSlug: 'chess-master', 
          difficulty: 'advance', 
          isGuest: true 
        });
        setAiDepth(2);
      }
    } else {
      // Allow guest play - create default session
      setSession({ 
        gameSlug: 'chess-master', 
        difficulty: 'advance', 
        isGuest: true 
      });
      setAiDepth(2);
    }
  }, [navigate]);

  // Timer
  useEffect(() => {
    let timer;
    if (!gameOver && session) {
      timer = setInterval(() => {
        setTimeElapsed(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [gameOver, session]);

  // Play sound effect
  const playSound = useCallback((type) => {
    if (!soundEnabled) return;
    // Simple audio feedback using Web Audio API
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    if (type === 'move') {
      oscillator.frequency.value = 440;
      gainNode.gain.value = 0.1;
    } else if (type === 'capture') {
      oscillator.frequency.value = 330;
      gainNode.gain.value = 0.15;
    } else if (type === 'check') {
      oscillator.frequency.value = 550;
      gainNode.gain.value = 0.2;
    }
    
    oscillator.start();
    oscillator.stop(audioContext.currentTime + 0.1);
  }, [soundEnabled]);

  // Check if game is over
  const checkGameOver = useCallback(() => {
    if (game.isGameOver()) {
      setGameOver(true);
      
      if (game.isCheckmate()) {
        const loser = game.turn();
        setWinner(loser === 'w' ? 'black' : 'white');
      } else if (game.isDraw()) {
        setWinner('draw');
      }
    }
  }, [game]);

  // Simple AI move using minimax with alpha-beta pruning
  const makeAIMove = useCallback(() => {
    if (game.isGameOver()) return;
    
    setIsThinking(true);
    
    // Use setTimeout to not block UI
    setTimeout(() => {
      // Create a temporary game copy for calculations
      const tempGame = new Chess(game.fen());
      const moves = tempGame.moves();
      if (moves.length === 0) {
        setIsThinking(false);
        return;
      }

      // Simple evaluation function
      const evaluateBoard = (board) => {
        const pieceValues = { p: 1, n: 3, b: 3, r: 5, q: 9, k: 0 };
        let score = 0;
        
        board.forEach(row => {
          row.forEach(piece => {
            if (piece) {
              const value = pieceValues[piece.type];
              score += piece.color === 'w' ? value : -value;
            }
          });
        });
        
        return score;
      };

      // Minimax with alpha-beta pruning (uses tempGame for calculations)
      const minimax = (depth, isMaximizing, alpha, beta) => {
        if (depth === 0 || tempGame.isGameOver()) {
          return evaluateBoard(tempGame.board());
        }

        const moves = tempGame.moves();
        
        if (isMaximizing) {
          let maxEval = -Infinity;
          for (const move of moves) {
            tempGame.move(move);
            const evaluation = minimax(depth - 1, false, alpha, beta);
            tempGame.undo();
            maxEval = Math.max(maxEval, evaluation);
            alpha = Math.max(alpha, evaluation);
            if (beta <= alpha) break;
          }
          return maxEval;
        } else {
          let minEval = Infinity;
          for (const move of moves) {
            tempGame.move(move);
            const evaluation = minimax(depth - 1, true, alpha, beta);
            tempGame.undo();
            minEval = Math.min(minEval, evaluation);
            beta = Math.min(beta, evaluation);
            if (beta <= alpha) break;
          }
          return minEval;
        }
      };

      // Find best move for black (AI)
      let bestMove = moves[0];
      let bestValue = Infinity;

      for (const move of moves) {
        tempGame.move(move);
        const value = minimax(aiDepth - 1, true, -Infinity, Infinity);
        tempGame.undo();
        
        if (value < bestValue) {
          bestValue = value;
          bestMove = move;
        }
      }

      // Make the AI move on a new game instance
      const gameCopy = new Chess(game.fen());
      const moveResult = gameCopy.move(bestMove);
      setGame(gameCopy);
      setGamePosition(gameCopy.fen());
      setMoveHistory(prev => [...prev, { color: 'black', move: bestMove, notation: moveResult.san }]);
      
      if (moveResult.captured) {
        playSound('capture');
      } else {
        playSound('move');
      }

      if (gameCopy.inCheck()) {
        playSound('check');
      }

      // Check for game over
      checkGameOver();
      setIsThinking(false);
    }, 500);
  }, [game, aiDepth, playSound, checkGameOver]);

  // Handle player move - SIMPLE VERSION
  const onDrop = useCallback((sourceSquare, targetSquare) => {
    try {
      const move = game.move({
        from: sourceSquare,
        to: targetSquare,
        promotion: 'q'
      });

      if (move === null) return false;

      setGamePosition(game.fen());
      
      // AI moves after short delay
      if (!game.isGameOver()) {
        setTimeout(() => makeAIMove(), 500);
      }

      return true;
    } catch (e) {
      return false;
    }
  }, [game, makeAIMove]);

  // Handle square click for click-to-move
  // react-chessboard v5 passes (square) as single argument
  const onSquareClick = (square) => {
    if (gameOver || isThinking) return;
    if (game.turn() !== playerColor[0]) return;

    const piece = game.get(square);
    
    // If a square is already selected
    if (selectedSquare) {
      // Try to move to the clicked square
      if (possibleMoves.includes(square)) {
        try {
          const gameCopy = new Chess(game.fen());
          const move = gameCopy.move({
            from: selectedSquare,
            to: square,
            promotion: 'q' // Auto-promote to queen
          });

          if (move) {
            setGame(gameCopy);
            setGamePosition(gameCopy.fen());
            setMoveHistory(prev => [...prev, { color: 'white', move: `${selectedSquare}${square}`, notation: move.san }]);

            if (move.captured) {
              playSound('capture');
            } else {
              playSound('move');
            }

            if (gameCopy.inCheck()) {
              playSound('check');
            }

            // Clear selection
            setSelectedSquare(null);
            setPossibleMoves([]);

            // Check for game over after player move
            if (gameCopy.isGameOver()) {
              checkGameOver();
            } else {
              // AI's turn
              setTimeout(() => makeAIMove(), 300);
            }
            return;
          }
        } catch (e) {
          console.error('Click move error:', e);
        }
      }
      
      // If clicked on own piece, select it
      if (piece && piece.color === playerColor[0]) {
        setSelectedSquare(square);
        const moves = game.moves({ square, verbose: true });
        setPossibleMoves(moves.map(m => m.to));
        return;
      }
      
      // Clear selection
      setSelectedSquare(null);
      setPossibleMoves([]);
      return;
    }

    // No square selected, select this piece if it's player's
    if (piece && piece.color === playerColor[0]) {
      setSelectedSquare(square);
      const moves = game.moves({ square, verbose: true });
      setPossibleMoves(moves.map(m => m.to));
    }
  };

  // Custom square styles for highlighting
  const customSquareStyles = useMemo(() => {
    const styles = {};
    
    if (selectedSquare) {
      styles[selectedSquare] = {
        backgroundColor: 'rgba(255, 255, 0, 0.4)'
      };
    }
    
    possibleMoves.forEach(square => {
      const piece = game.get(square);
      styles[square] = {
        background: piece 
          ? 'radial-gradient(circle, rgba(255,0,0,0.4) 85%, transparent 85%)'
          : 'radial-gradient(circle, rgba(0,255,0,0.3) 25%, transparent 25%)',
        borderRadius: '50%'
      };
    });
    
    return styles;
  }, [selectedSquare, possibleMoves, game]);

  // End game and submit result
  const endGame = async (playerWon) => {
    if (!session || endingGame || session.isGuest) {
      // For guest play, just show result without API call
      if (session?.isGuest) {
        setResult({ message: playerWon ? 'You won!' : 'Better luck next time!' });
        toast({
          title: playerWon ? "You Won!" : "Game Over",
          description: playerWon ? "Great game! Login to earn rewards." : "Better luck next time!",
        });
      }
      return;
    }
    
    setEndingGame(true);

    try {
      const res = await api.post('/games/chess-master/end', {
        session_id: session.sessionId,
        score: moveHistory.length,
        won: playerWon,
        time_played_seconds: timeElapsed
      });

      setResult(res.data);
      localStorage.removeItem('currentGameSession');
      
      toast({
        title: playerWon ? "You Won!" : "Game Over",
        description: playerWon && res.data.reward_earned > 0 
          ? `You earned ${res.data.reward_earned} AGP!` 
          : res.data.message,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to end game",
        variant: "destructive"
      });
    } finally {
      setEndingGame(false);
    }
  };

  // Change difficulty
  const changeDifficulty = (level) => {
    const depths = { easy: 1, medium: 2, hard: 3 };
    setAiDepth(depths[level] || 2);
    toast({
      title: "Difficulty Changed",
      description: `AI is now playing at ${level} level`,
    });
    resetGame();
  };

  // Handle game over
  useEffect(() => {
    if (gameOver && winner && !result) {
      const playerWon = winner === playerColor;
      endGame(playerWon);
    }
  }, [gameOver, winner, playerColor, result]);

  // Reset game
  const resetGame = () => {
    const newGame = new Chess();
    setGame(newGame);
    setGamePosition(newGame.fen());
    setMoveHistory([]);
    setGameOver(false);
    setWinner(null);
  };

  // Resign game
  const handleResign = () => {
    if (confirm('Are you sure you want to resign?')) {
      setGameOver(true);
      setWinner('black');
    }
  };

  // Format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Result screen
  if (result) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="chess-result-screen">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 text-center">
            {winner === playerColor ? (
              <>
                <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Trophy className="w-10 h-10 text-yellow-400" />
                </div>
                <h2 className="text-2xl font-bold text-green-400 mb-2">Checkmate! You Won!</h2>
              </>
            ) : winner === 'draw' ? (
              <>
                <div className="w-20 h-20 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Crown className="w-10 h-10 text-yellow-400" />
                </div>
                <h2 className="text-2xl font-bold text-yellow-400 mb-2">Draw!</h2>
              </>
            ) : (
              <>
                <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Crown className="w-10 h-10 text-red-400" />
                </div>
                <h2 className="text-2xl font-bold text-red-400 mb-2">Checkmate! AI Wins</h2>
              </>
            )}

            <div className="space-y-3 my-6">
              <div className="flex justify-between p-3 bg-muted rounded-lg">
                <span>Moves Played</span>
                <span className="font-bold">{moveHistory.length}</span>
              </div>
              <div className="flex justify-between p-3 bg-muted rounded-lg">
                <span>Time</span>
                <span className="font-bold">{formatTime(timeElapsed)}</span>
              </div>
              {result.reward_earned > 0 && (
                <div className="flex justify-between p-3 bg-yellow-500/20 rounded-lg">
                  <span>Reward</span>
                  <span className="font-bold text-yellow-400">+{result.reward_earned} AGP</span>
                </div>
              )}
              <div className="flex justify-between p-3 bg-muted rounded-lg">
                <span>New Balance</span>
                <span className="font-bold text-yellow-400">{result.new_agp_balance?.toFixed(0)} AGP</span>
              </div>
            </div>

            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => navigate('/games')}>
                Game Zone
              </Button>
              <Button className="flex-1" onClick={() => navigate('/games')}>
                Play Again
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="chess-game-page">
      {/* HUD */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b">
        <div className="max-w-4xl mx-auto px-2 sm:px-4 py-2 sm:py-3">
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="sm" onClick={() => {
              if (confirm('Exit game? This will count as a loss.')) {
                  endGame(false);
                }
            }}>
              <ArrowLeft className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Exit</span>
            </Button>

            <div className="flex items-center gap-2 sm:gap-4">
              <div className="flex items-center gap-1 sm:gap-2 bg-muted px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm">
                <Clock className="w-3 h-3 sm:w-4 sm:h-4 text-blue-400" />
                <span className="font-mono font-bold">{formatTime(timeElapsed)}</span>
              </div>

              <div className="flex items-center gap-1 sm:gap-2 bg-muted px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm">
                <Trophy className="w-3 h-3 sm:w-4 sm:h-4 text-yellow-400" />
                <span className="font-bold">{moveHistory.length}</span>
              </div>
            </div>

            <div className="flex items-center gap-1 sm:gap-2">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setSoundEnabled(!soundEnabled)}
              >
                {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
              </Button>
              
              {/* Difficulty Selector */}
              <select
                value={aiDepth === 1 ? 'easy' : aiDepth === 3 ? 'hard' : 'medium'}
                onChange={(e) => changeDifficulty(e.target.value)}
                className="text-xs sm:text-sm bg-muted border-none rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Game Area */}
      <div className="max-w-4xl mx-auto p-2 sm:p-4">
        <div className="flex flex-col lg:grid lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Chessboard */}
          <div className="lg:col-span-2">
            <Card className="p-2 sm:p-4">
              {/* AI indicator */}
              <div className="flex items-center justify-between mb-2 sm:mb-3 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-gray-800" />
                  <span className="font-medium">AI (Black)</span>
                  {isThinking && <span className="text-xs text-muted-foreground animate-pulse">Thinking...</span>}
                </div>
                {game.turn() === 'b' && !gameOver && (
                  <span className="text-xs px-2 py-1 bg-primary/20 text-primary rounded">Their turn</span>
                )}
              </div>

              {/* Board - Responsive */}
              <div className="aspect-square w-full max-w-[min(100vw-2rem,500px)] mx-auto">
                <Chessboard
                  id="ChessBoard"
                  position={gamePosition}
                  onPieceDrop={onDrop}
                  boardOrientation="white"
                  arePiecesDraggable={true}
                />
              </div>

              {/* Player indicator */}
              <div className="flex items-center justify-between mt-2 sm:mt-3 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-white border" />
                  <span className="font-medium">You (White)</span>
                </div>
                {game.turn() === 'w' && !gameOver && (
                  <span className="text-xs px-2 py-1 bg-green-500/20 text-green-400 rounded">Your turn</span>
                )}
              </div>

              {/* Game status */}
              {game.inCheck() && !gameOver && (
                <div className="mt-2 sm:mt-3 p-2 bg-red-500/20 text-red-400 rounded text-center font-bold text-sm">
                  CHECK!
                </div>
              )}
            </Card>

            {/* Mobile Resign Button */}
            <div className="lg:hidden mt-4">
              <Button 
                variant="outline" 
                className="w-full"
                onClick={handleResign}
                disabled={gameOver}
              >
                <Heart className="w-4 h-4 mr-2 text-red-400" />
                Resign
              </Button>
            </div>
          </div>

          {/* Side Panel - Hidden on mobile, shown on large screens */}
          <div className="hidden lg:block space-y-4">
            {/* Actions */}
            <Card>
              <CardContent className="p-4 space-y-3">
                <h3 className="font-bold">Actions</h3>
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={handleResign}
                  disabled={gameOver}
                >
                  <Heart className="w-4 h-4 mr-2 text-red-400" />
                  Resign
                </Button>
              </CardContent>
            </Card>

            {/* Move History */}
            <Card>
              <CardContent className="p-4">
                <h3 className="font-bold mb-3">Move History</h3>
                <div className="max-h-[200px] overflow-y-auto space-y-1 text-sm">
                  {moveHistory.length === 0 ? (
                    <p className="text-muted-foreground text-center py-4">No moves yet</p>
                  ) : (
                    moveHistory.map((move, idx) => (
                      <div 
                        key={idx}
                        className={`flex justify-between p-1.5 rounded ${
                          move.color === 'white' ? 'bg-white/10' : 'bg-gray-800/30'
                        }`}
                      >
                        <span className="text-muted-foreground">{Math.floor(idx/2) + 1}.</span>
                        <span className="font-mono">{move.notation}</span>
                        <span className="text-xs text-muted-foreground capitalize">{move.color}</span>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Tips */}
            <Card>
              <CardContent className="p-4">
                <h3 className="font-bold flex items-center gap-2 mb-2">
                  <Lightbulb className="w-4 h-4 text-yellow-400" />
                  Tips
                </h3>
                <ul className="text-xs text-muted-foreground space-y-1">
                  <li>• Control the center of the board</li>
                  <li>• Develop your pieces early</li>
                  <li>• Castle to protect your king</li>
                  <li>• Think ahead before moving</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChessGame;
